import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { saveAs } from 'file-saver';
import {
  ButtonComponent,
  EmptyComponent,
  SkeletonComponent,
} from 'nebula-ui-kit';
import { finalize } from 'rxjs';
import { PreservationService } from '../../../../data-access/src/lib/preservation/preservation.service';
import type {
  PreservationStatistics,
  PreservationStatisticsByExtension,
} from '../../../../domain/src/lib/preservation/preservation.model';
import {
  ExportFormatOption,
  ExportFormatSelectorComponent,
  applyVaultModuleInitialLoadErrorSignals,
  PageHeaderComponent,
  toModuleAccessErrorMessage,
  VaultModuleShellComponent,
} from '@nebula-shared/ui';

interface StatusRow {
  label: string;
  value: number;
  percent: number;
}

interface ExtensionRow {
  extension: string;
  count: number;
  optimal: number;
  attention: number;
  critical: number;
  unverified: number;
}

interface ExportRequestParams {
  allRecords?: boolean;
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PageHeaderComponent,
    ButtonComponent,
    EmptyComponent,
    SkeletonComponent,
    ExportFormatSelectorComponent,
    VaultModuleShellComponent,
  ],
})
export class ReportsComponent {
  private readonly preservationService = inject(PreservationService);
  private readonly destroyRef = inject(DestroyRef);

  private static readonly MODULE_LABEL = 'Reportes Generales';

  readonly loading = signal(true);
  readonly refreshing = signal(false);
  readonly accessDenied = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorTitle = signal('');
  readonly error = signal<string | null>(null);
  readonly stats = signal<PreservationStatistics | null>(null);
  readonly lastUpdated = signal<Date | null>(null);
  readonly exportOptions: ExportFormatOption[] = [
    { value: 'excel', label: 'Excel (.xls)' },
    { value: 'csv', label: 'CSV (.csv)' },
  ];

  readonly statusRows = computed<StatusRow[]>(() => {
    const s = this.stats();
    if (!s) return [];
    return [
      { label: 'Óptimos', value: s.by_status.optimal, percent: this.percentage(s.by_status.optimal, s.total_files) },
      {
        label: 'Requieren atención',
        value: s.by_status.attention,
        percent: this.percentage(s.by_status.attention, s.total_files),
      },
      { label: 'Críticos', value: s.by_status.critical, percent: this.percentage(s.by_status.critical, s.total_files) },
      {
        label: 'Sin verificar',
        value: s.by_status.unverified,
        percent: this.percentage(s.by_status.unverified, s.total_files),
      },
    ];
  });

  readonly topExtensions = computed<ExtensionRow[]>(() => {
    const byExt = this.stats()?.by_extension ?? {};
    return this.mapExtensions(byExt).slice(0, 10);
  });

  readonly allExtensions = computed<ExtensionRow[]>(() => {
    const byExt = this.stats()?.by_extension ?? {};
    return this.mapExtensions(byExt);
  });

  constructor() {
    this.refreshStatistics(true);
  }

  refreshStatistics(firstLoad = false): void {
    if (firstLoad) this.loading.set(true);
    else this.refreshing.set(true);

    this.error.set(null);
    this.accessDenied.set(false);
    this.loadError.set(null);
    this.preservationService
      .getStatistics()
      .pipe(
        finalize(() => {
          if (firstLoad) this.loading.set(false);
          else this.refreshing.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => {
          this.stats.set(data);
          this.lastUpdated.set(new Date());
        },
        error: (err: unknown) =>
          applyVaultModuleInitialLoadErrorSignals(err, {
            moduleLabel: ReportsComponent.MODULE_LABEL,
            fallback: 'No se pudieron cargar las estadísticas de preservación.',
          }, {
            accessDenied: this.accessDenied,
            loadError: this.loadError,
            loadErrorTitle: this.loadErrorTitle,
          }),
      });
  }

  exportExcel(): void {
    const s = this.stats();
    if (!s) return;

    const rows: string[] = [];
    rows.push('<table border="1">');
    rows.push('<tr><th>Sección</th><th>Item</th><th>Valor</th><th>Detalle 1</th><th>Detalle 2</th></tr>');
    rows.push(
      `<tr><td>Resumen</td><td>Total de ingestas (paquetes AIP)</td><td>${s.total_files}</td><td>Paquetes .zip preservados</td><td></td></tr>`,
    );
    const resolvedSizeMb = this.resolveTotalSizeMb(s);
    rows.push(
      `<tr><td>Resumen</td><td>Tamaño total</td><td>${this.escapeHtml(this.formatSizeFromStats(s))}</td><td>${resolvedSizeMb !== null ? `${resolvedSizeMb.toFixed(3)} MB` : 'Sin dato'}</td><td></td></tr>`,
    );
    rows.push(
      `<tr><td>Resumen</td><td>Paquetes AIP con custodia legal</td><td>${s.files_with_legal_hold}</td><td></td><td></td></tr>`,
    );
    rows.push(
      `<tr><td>Resumen</td><td>Paquetes AIP con retención activa</td><td>${s.files_with_retention}</td><td></td><td></td></tr>`,
    );

    for (const r of this.statusRows()) {
      rows.push(
        `<tr><td>Estado</td><td>${this.escapeHtml(r.label)}</td><td>${r.value}</td><td>${r.percent.toFixed(1)}%</td><td></td></tr>`,
      );
    }

    for (const r of this.allExtensions()) {
      rows.push(
        `<tr><td>Extensión del paquete AIP</td><td>${this.escapeHtml(r.extension)}</td><td>${r.count}</td><td>Óptimos:${r.optimal} | Atención:${r.attention}</td><td>Críticos:${r.critical} | Sin verificar:${r.unverified}</td></tr>`,
      );
    }
    rows.push('</table>');

    const htmlDoc = [
      '<html><head><meta charset="UTF-8"></head><body>',
      ...rows,
      '</body></html>',
    ].join('');
    const blob = new Blob([`\uFEFF${htmlDoc}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    this.downloadBlob(blob, `reportes-avanzados-${new Date().toISOString().slice(0, 10)}.xls`);
  }

  exportCsv(params?: ExportRequestParams): void {
    const s = this.stats();
    if (!s) return;

    const extensions = params?.allRecords ? this.allExtensions() : this.topExtensions();
    const lines: string[] = [];
    lines.push('seccion;item;valor;detalle_1;detalle_2');
    lines.push(`Resumen;Total de ingestas (paquetes AIP);${s.total_files};Paquetes .zip preservados;`);
    lines.push(`Resumen;Tamaño acumulado de paquetes AIP;${this.formatSizeFromStats(s)};;`);
    lines.push(`Resumen;Paquetes AIP con custodia legal;${s.files_with_legal_hold};;`);
    lines.push(`Resumen;Paquetes AIP con retención activa;${s.files_with_retention};;`);

    for (const row of this.statusRows()) {
      lines.push(`Estado;${row.label};${row.value};${row.percent.toFixed(1)}%;`);
    }

    for (const row of extensions) {
      lines.push(
        `Extensión del paquete AIP;${row.extension};${row.count};Óptimos:${row.optimal} | Atención:${row.attention};Críticos:${row.critical} | Sin verificar:${row.unverified}`,
      );
    }

    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, `reportes-avanzados-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  onExportFormatSelected(format: string, params?: ExportRequestParams): void {
    if (format === 'excel') {
      this.exportExcel();
      return;
    }
    if (format === 'csv') {
      this.exportCsv(params);
    }
  }

  formatInt(value: number): string {
    return new Intl.NumberFormat('es-CO').format(value);
  }

  formatSizeFromMb(valueMb: number): string {
    if (!Number.isFinite(valueMb) || valueMb <= 0) return '0 MB';

    if (valueMb < 1) {
      const kb = valueMb * 1024;
      return `${kb.toFixed(2)} KB`;
    }

    if (valueMb < 1024) {
      return `${valueMb.toFixed(2)} MB`;
    }

    const gb = valueMb / 1024;
    return `${gb.toFixed(2)} GB`;
  }

  formatSizeFromStats(stats: PreservationStatistics | null): string {
    if (!stats) return 'Sin dato';
    const sizeMb = this.resolveTotalSizeMb(stats);
    if (sizeMb === null || sizeMb <= 0) {
      return stats.total_files > 0 ? 'Pendiente de cálculo' : '0 MB';
    }
    return this.formatSizeFromMb(sizeMb);
  }

  formatUpdatedAt(value: Date | null): string {
    return value ? value.toLocaleTimeString() : '—';
  }

  private percentage(value: number, total: number): number {
    if (!total) return 0;
    return (value / total) * 100;
  }

  private mapExtensions(byExt: Record<string, PreservationStatisticsByExtension>): ExtensionRow[] {
    return Object.entries(byExt)
      .map(([extension, data]) => ({
        extension: extension || 'sin_ext',
        count: data.count,
        optimal: data.by_status.optimal,
        attention: data.by_status.attention,
        critical: data.by_status.critical,
        unverified: data.by_status.unverified,
      }))
      .sort((a, b) => b.count - a.count);
  }

  private resolveTotalSizeMb(stats: PreservationStatistics): number | null {
    if (Number.isFinite(stats.total_size_mb) && stats.total_size_mb > 0) {
      return stats.total_size_mb;
    }

    const sizeByStatus = stats.size_by_status;
    const statusSum =
      (sizeByStatus?.optimal ?? 0) +
      (sizeByStatus?.attention ?? 0) +
      (sizeByStatus?.critical ?? 0) +
      (sizeByStatus?.unverified ?? 0);
    if (Number.isFinite(statusSum) && statusSum > 0) {
      return statusSum;
    }

    const rec = stats as unknown as Record<string, unknown>;
    const bytes = Number(rec['total_size_bytes']);
    if (Number.isFinite(bytes) && bytes > 0) {
      return bytes / (1024 * 1024);
    }

    const fallback = Number(rec['total_size']);
    if (Number.isFinite(fallback) && fallback > 0) {
      return fallback;
    }

    return null;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
    saveAs(file);
  }
}
